import React, { useEffect, useState } from "react";
import Navbar from "@/components/Navbar/Navbar";
import SideMenu from "@/components/MyAccount/SideMenu";
import ApiHttpClient from "@/HttpClient";
import { UsersDetailsModel, WarehouseModel, WorkingPointModel } from "@/api/Models";
import { useParams } from "react-router";
import { toast } from "@/components/ui/use-toast";
import { getDecodedTokenDetails } from "@/components/Token/TokenService";
import UserFormPage, {UserFormMode} from "@/components/MyAccount/UserManagement/UserFormPage";

export interface UserFormValues {
    accountName: string;
    userName: string;
    password: string;
    phoneNumber: string;
    role: string;
    uc: string;
    pairWorkingPointStore: string;
    selectedWorkingPointId: string;
}

interface WorkingPointResponse {
    items: WorkingPointModel[];
    pageNumber: number;
    pageSize: number;
    totalCount: number;
    totalPages: number;
    hasPrevious: boolean;
    hasNext: boolean;
}
type Role =
    | "BASIC"
    | "BASICSUPPORT"
    | "B2BSUPPORT"
    | "SUPERBASIC"
    | "AUCHANMBAEMPLOYEE"
    | "ADMIN"
    | "SUPPORTL1"
    | "SUPERADMIN"
    | "SERVICE"
    | "SUPPORTL2L3";

const ROLE_HIERARCHY: Record<Role, any> = {
    BASIC: 0,
    BASICSUPPORT: 1,
    B2BSUPPORT: 1,
    SUPERBASIC: 2,
    AUCHANMBAEMPLOYEE: 3,
    ADMIN: 4,
    SUPPORTL1: 5,
    SUPERADMIN: 6,
    SERVICE: 7,
    SUPPORTL2L3: 8,
};

const ROLE_OPTIONS: Role[] = [
    "BASIC",
    "BASICSUPPORT",
    "B2BSUPPORT",
    "SUPERBASIC",
    "AUCHANMBAEMPLOYEE",
    "ADMIN",
    "SUPPORTL1",
    "SUPERADMIN",
    "SERVICE",
    "SUPPORTL2L3",
];
function ChangeUserPage() {
    const queryParams = useParams();
    const userId = queryParams.userId;
    const [userDate, setUserDate] = useState<UsersDetailsModel | null>();
    const [selectedRole, setSelectedRole] = useState<string>("");
    const [selectedUC, setSelectedUC] = useState<string>("");
    const [warehouses, setWarehouses] = useState<any>();
    const [workingPoints, setWorkingPoints] = useState<WorkingPointModel[]>([]);
    const [selectedWorkingPointId, setSelectedWorkingPointId] = useState<string>("");
    const [availableRoles, setAvailableRoles] = useState<string[]>([]);
    const [pairWorkingPointStore, setPairWorkingPointStore] = useState<any>();

    const handleUpdateUser =  async (formData: UserFormValues) =>  {
        if (!userDate) return;

        const payload = {
            userId: userId,
            email: userDate.userDetails.accountName,
            userName: userDate.userDetails.name,
            role: formData.role,
            claims: (() => {
                const claimsObject: Record<string, string[]> = {};

                if (formData.role === "BASIC" && formData.selectedWorkingPointId) {
                    claimsObject["WPID"] = [formData.selectedWorkingPointId];
                }

                if ((formData.role === "BASICSUPPORT" || formData.role === "SUPERBASIC" || formData.role === "B2BSUPPORT" || formData.role === "ADMIN") && formData.uc) {
                    claimsObject["ADMINORG"] = [formData.uc];
                }

                if ((formData.role === "AUCHANMBAEMPLOYEE") && formData.pairWorkingPointStore) {
                    claimsObject["MBASTORE"] = [formData.pairWorkingPointStore.toString()];
                }
                return claimsObject;
            })(),

        };

        try {
            const apiBase = process.env.REACT_APP_AIS_ENDPOINT?.replace(/\/v1\/?$/, '') ?? '';
            await ApiHttpClient().put(`${apiBase}/users?userId=${userId}`, payload);
            toast({ description: "Utilizatorul a fost actualizat cu succes!" });
        } catch (error) {
            toast({ description: "A apărut o eroare la actualizarea utilizatorului" });
            console.error(error);
        }
    };

    function isRole(role: string): role is Role {
        return role in ROLE_HIERARCHY;
    }

    useEffect(() => {
        (async () => {
            try {
                const tokenDetails = await getDecodedTokenDetails();

                if (tokenDetails?.token) {
                    const userRoleStr = tokenDetails.userRole.toString();

                    if (!isRole(userRoleStr)) {
                        console.error("Invalid user role:", userRoleStr);
                        return;
                    }

                    const userRole: Role = userRoleStr;
                    const userRoleValue = ROLE_HIERARCHY[userRole];

                    const filteredRoles = ROLE_OPTIONS.filter(
                        (role) => ROLE_HIERARCHY[role] < userRoleValue
                    );

                    setAvailableRoles(filteredRoles);
                } else {
                    console.error("No token found");
                }
            } catch (error) {
                console.error("Failed to decode token", error);
            }
        })();
    }, []);


    useEffect(() => {
        const getUserDetails = async () => {
            const response = await ApiHttpClient().get<UsersDetailsModel>(`/users/userManagement/${userId}`
            );

            if (!response?.data) {
                toast({ description: "A aparut o eroare la preluarea informatiilor despre acest user" });
                return <></>;
            }

            setUserDate(response?.data);
            const initialRole = response.data.userDetails?.roles?.[0]?.roleName || "";
            setSelectedRole(initialRole);
            if ((response.data.userDetails.claims[0]?.key as string) === "ADMINORG")
                setSelectedUC(response.data.userDetails.claims[0]?.value[0])
            else setSelectedUC("")
           
            setSelectedWorkingPointId(response.data?.workingPoint?.workingPointId);
            
            if((response.data.userDetails.claims[0]?.key as string) === "MBASTORE"){
                setPairWorkingPointStore(response.data?.mbaStore?.warehouseId);
            }
            
        }

        getUserDetails()
    }, [userId]);

    useEffect(() => {
        const fetchWorkingPoints = async () => {
            try {
                const response = await ApiHttpClient().post<WorkingPointResponse>(
                    `/workingPoint/search`,
                    {
                        pageNumber: 1,
                        pageSize: 100,
                        orderBy: ["workingPointName"],
                    }
                );
                setWorkingPoints(response.data.items || []);
            } catch (error) {
                console.error("Failed to fetch working points", error);
            }
        };

        fetchWorkingPoints();
    }, []);

    useEffect(() => {
        const fetchWarehouses = async () => {
            let response = await ApiHttpClient().post<WarehouseModel>(`/warehouse/search`, {
                "advancedSearch": {
                    "fields": [
                        "warehousePlatform"
                    ],
                    "keyword": "MAGAZIN",
                },
            });

            setWarehouses(response.data);
        };

        fetchWarehouses();
    }, []);

    useEffect(() => {
        if (selectedRole === "BASICSUPPORT" || selectedRole === "SUPERBASIC") {
            setSelectedUC("816");
        } else if (selectedRole === "B2BSUPPORT") {
            setSelectedUC("817");
        } else if (selectedRole === "ADMIN") {
            setSelectedUC("");
        } else {
            setSelectedUC("");
        }
    }, [selectedRole]);


    return (
        <>
            <Navbar />
            <div className="flex flex-row items-start">
                <SideMenu />
                <div className="flex flex-col overflow-auto w-full">
                    <div className="text-4xl px-8 py-3 mt-5">Modifica datele unui utilizator</div>
                    <div className="overflow-x-auto px-8 py-2 mt-2">

                        <form className="needs-validation peer grid gap-y-4" noValidate>
                            <div className="w-full">
                                <h6 className="text-xl font-semibold">Informatii Needitabile</h6>
                                <hr className="mb-4 mt-2" />
                            </div>

                            <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
                                <div>
                                    <label htmlFor="userEmail" className="flex flex-col">
                                        <span className="text-lg font-medium text-gray-700"> User Email </span>

                                        <input
                                            type="text"
                                            id="userEmail"
                                            disabled
                                            placeholder="userEmail"
                                            value={userDate?.userDetails.accountName}
                                            className="mt-0.5 w-1/2 bg-white rounded py-1 border-gray-300 shadow-sm sm:text-sm"
                                        />
                                    </label>
                                </div>

                                <div>
                                    <label htmlFor="userName" className="flex flex-col">
                                        <span
                                            className="text-lg font-medium text-gray-700 flex flex-row"> Numele </span>

                                        <input
                                            type="text"
                                            id="userName"
                                            disabled
                                            placeholder="userName"
                                            value={userDate?.userDetails.name}
                                            className="mt-0.5 w-1/2 bg-white rounded py-1 border-gray-300 shadow-sm sm:text-sm"
                                        />
                                    </label>
                                </div>
                            </div>

                            <div className="w-full mt-10">
                                <h6 className="text-xl font-semibold">Informatii Editabile</h6>
                                <hr className="mb-4 mt-2" />
                            </div>

                            {userDate && (
                                <UserFormPage
                                    mode={UserFormMode.EDIT}
                                    initialValues={{
                                        accountName: userDate.userDetails.accountName,
                                        userName: userDate.userDetails.name,
                                        password: "",
                                        phoneNumber: "",
                                        role: selectedRole,
                                        uc: selectedUC,
                                        pairWorkingPointStore: pairWorkingPointStore,
                                        selectedWorkingPointId: selectedWorkingPointId,
                                    }}
                                    availableRoles={availableRoles}
                                    workingPoints={workingPoints}
                                    warehouses={warehouses}
                                    onSubmit={(data) => {
                                        handleUpdateUser(data);
                                    }}
                                />
                            )}

                        </form>
                    </div>
                </div>
            </div>
        </>
    );
}

export default ChangeUserPage;
